import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router';

import { RowDblClickEvent } from 'devextreme/ui/data_grid';

import DataGrid, {
  Column,
  ColumnChooser,
  Export,
  FilterPanel,
  FilterRow,
  GroupPanel,
  HeaderFilter,
  Lookup,
  Pager,
  Paging,
  StateStoring,
  Summary,
  TotalItem,
} from 'devextreme-react/data-grid';

import moment from 'moment';

import { AppCard, AppLayoutTop } from 'components/appComponents';
import { Button } from 'devextreme-react';
import $createStore from 'utils/create-store-instance';
import { onExportExcel } from 'utils/exportExcel';

import {
  useGetRequestStatusesQuery,
  useLazyGetArchiveRequestWorkExcelReportQuery,
  useLazyGetPaymentMethodReportQuery,
  useLazyGetRequestDestinyQuery,
} from 'bus/request';

const EXPORT_REPORTS_NEED_FILTER_HINT = 'нужно выбрать фильтр для выгрузки';

const hasCombinedFilterApplied = (filtering: unknown): boolean => {
  if (filtering === undefined || filtering === null || filtering === '') {
    return false;
  }
  if (Array.isArray(filtering) && filtering.length === 0) {
    return false;
  }
  return true;
};

const isFilterRelatedGridOptionChanged = (fullName?: string): boolean => {
  if (!fullName) {
    return false;
  }
  return fullName.includes('filter');
};

const TaskArch: FC = () => {
  const history = useHistory();

  const requestDateSource: any = useMemo(() => $createStore('/v1/Request/Archived', 'id'), []);
  const paymentMethodsDataSource = useMemo(() => {
    return $createStore('/v1/ContractorNomenclaturePrice/types', 'id');
  }, []);

  const refGrid = useRef<DataGrid | null>(null);
  const [hasActiveFilter, setHasActiveFilter] = useState(false);
  const [getRequestDestiny] = useLazyGetRequestDestinyQuery();
  const [getArchiveRequestWorkExcelReport, { isLoading: isLoadingArchiveRequestWorkExcelReport }] =
    useLazyGetArchiveRequestWorkExcelReportQuery();
  const { data: requestStatuses } = useGetRequestStatusesQuery();

  useEffect(() => {
    getRequestDestiny();
  }, [getRequestDestiny]);

  const [getPaymentMethodReport, { isLoading }] = useLazyGetPaymentMethodReportQuery();

  const syncHasActiveFilter = useCallback(() => {
    const instance = refGrid.current?.instance;
    if (!instance) {
      setHasActiveFilter(false);
      return;
    }
    setHasActiveFilter(hasCombinedFilterApplied(instance.getCombinedFilter()));
  }, []);

  const handleGridOptionChanged = useCallback(
    (event: { fullName?: string; name?: string }) => {
      if (event.name === 'filterValue' || isFilterRelatedGridOptionChanged(event.fullName)) {
        syncHasActiveFilter();
      }
    },
    [syncHasActiveFilter],
  );

  const handleSubmit = async () => {
    const filtering = refGrid.current?.instance.getCombinedFilter();
    await getPaymentMethodReport(JSON.stringify(filtering));
  };

  const handleSubmitArchiveRequestWorkExcelReport = async () => {
    const filtering = refGrid.current?.instance.getCombinedFilter();
    await getArchiveRequestWorkExcelReport(JSON.stringify(filtering));
  };

  const shipmentsReportDisabled = !hasActiveFilter || isLoadingArchiveRequestWorkExcelReport;
  const paymentMethodReportDisabled = !hasActiveFilter || isLoading;

  const shipmentsReportButton = (
    <Button
      text="Отчет по отгрузкам"
      type="default"
      useSubmitBehavior={false}
      onClick={handleSubmitArchiveRequestWorkExcelReport}
      disabled={shipmentsReportDisabled}
      hint={!hasActiveFilter ? EXPORT_REPORTS_NEED_FILTER_HINT : undefined}
    />
  );

  const paymentMethodReportButton = (
    <Button
      text="Отчет по методу приема"
      type="default"
      useSubmitBehavior={false}
      onClick={handleSubmit}
      disabled={paymentMethodReportDisabled}
      hint={!hasActiveFilter ? EXPORT_REPORTS_NEED_FILTER_HINT : undefined}
    />
  );

  const ARCHIVE_GRID_STATE_KEY = 'requestArchivedV2';

  const loadGridState = useCallback(() => {
    try {
      const raw = localStorage.getItem(ARCHIVE_GRID_STATE_KEY);
      if (!raw) {
        return null;
      }
      const state = JSON.parse(raw) as Record<string, unknown>;
      delete state.grouping;
      return state;
    } catch {
      return null;
    }
  }, []);

  const saveGridState = useCallback((state: Record<string, unknown>) => {
    localStorage.setItem(ARCHIVE_GRID_STATE_KEY, JSON.stringify(state));
  }, []);

  const handleRowDblClick = useCallback(
    (e: RowDblClickEvent) => {
      if (e.rowType !== 'data') {
        return;
      }
      const id = e.data?.id ?? e.key;
      if (id == null || id === '') {
        return;
      }
      history.push(`/task/detail-arch/${id}`);
    },
    [history],
  );

  const handleContentReady = useCallback(
    (e: { component: { closeEditCell: () => void } }) => {
      e.component.closeEditCell();
      syncHasActiveFilter();
    },
    [syncHasActiveFilter],
  );

  return (
    <div className="container-dev">
      <AppLayoutTop title="Архивные заявки">
        <span className="d-inline-block mr-2">
          {!hasActiveFilter ? (
            <span title={EXPORT_REPORTS_NEED_FILTER_HINT}>{shipmentsReportButton}</span>
          ) : (
            shipmentsReportButton
          )}
        </span>
        <span className="d-inline-block">
          {!hasActiveFilter ? (
            <span title={EXPORT_REPORTS_NEED_FILTER_HINT}>{paymentMethodReportButton}</span>
          ) : (
            paymentMethodReportButton
          )}
        </span>
      </AppLayoutTop>

      <AppCard>
        <DataGrid
          dataSource={requestDateSource}
          showBorders={false}
          loadPanel={{
            enabled: true,
            text: 'Загрузка',
          }}
          ref={refGrid}
          onOptionChanged={handleGridOptionChanged}
          remoteOperations={{
            filtering: true,
            grouping: false,
            groupPaging: false,
            paging: true,
            sorting: true,
            summary: false,
          }}
          allowColumnResizing
          columnResizingMode="nextColumn"
          columnMinWidth={50}
          allowColumnReordering
          onExporting={onExportExcel}
          onRowDblClick={handleRowDblClick}
          onContentReady={handleContentReady}
          onRowPrepared={(e) => {
            if (e.rowType === 'data' && e.data.paymentMethod === 2 && !e.data.price) {
              //@ts-ignore
              e.rowElement.style.backgroundColor = '#fbaf5e';
            }

            if (e.rowType === 'data' && e.data.paymentMethod === 3) {
              //@ts-ignore
              e.rowElement.style.backgroundColor = '#fbaf5e';
            }
          }}
        >
          <Export enabled={true} />
          <StateStoring
            enabled={true}
            type="custom"
            customLoad={loadGridState}
            customSave={saveGridState}
          />
          <HeaderFilter visible />
          <GroupPanel visible={true} />
          <FilterRow visible={true} />
          <FilterPanel visible={true} />
          <ColumnChooser enabled={true} />
          <Column dataField="id" caption="Номер" width={80} />
          <Column dataField="requestStatus" caption="Статус" width={100}>
            <Lookup dataSource={requestStatuses} valueExpr="id" displayExpr="name" />
          </Column>
          <Column dataField="factoryName" caption="Площадка" />
          <Column
            width={180}
            defaultSortOrder="desc"
            sortOrder="desc"
            dataField="time"
            caption="Дата"
            dataType="date"
            cellRender={(data) => <div>{data.data.time && moment(data.data.time).format('DD.MM.yyyy HH.mm')}</div>}
          >
            <HeaderFilter groupInterval="day" />
          </Column>
          <Column dataField="paymentMethod" caption="Способ оплаты">
            <Lookup dataSource={paymentMethodsDataSource} valueExpr="id" displayExpr="name" />
          </Column>
          <Column dataField="dispatcher" caption="Диспетчер" />
          <Column dataField="contractor" caption="Заказчик" />
          <Column dataField="object" caption="Объект" />
          <Column
            dataField="nomenclatureName"
            caption="Марка (Плотность)"
            dataType="string"
            cellRender={({ data }) => <span>{`${data.nomenclatureName}(${data.density})`}</span>}
          />

          <Column dataField="quantity" caption="Объём" dataType="number" width="90" />
          <Column dataField="shipped" caption="Отлито" dataType="number" width="90" />
          <Summary>
            <TotalItem column="quantity" summaryType="sum" displayFormat="{0}" />
            <TotalItem column="shipped" summaryType="sum" displayFormat="{0}" />
          </Summary>

          <Paging defaultPageSize={100} />
          <Pager showPageSizeSelector={true} allowedPageSizes={[15, 30, 50, 100]} />
        </DataGrid>
      </AppCard>
    </div>
  );
};

export default TaskArch;
